import { createFileRoute, Link, Outlet, useMatches } from "@tanstack/react-router";
import { leafCanonicalLink, routeMeta, leafPathFromMatches } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { pergolaBreadcrumbScript } from "@/lib/pergolaJsonLd";
import { ServicePage } from "@/components/ServicePage";
import { PergolaUpgradeCta } from "@/components/PergolaUpgradeCta";
import { GazeboCoverForm } from "@/components/GazeboCoverForm";
import { Check } from "lucide-react";
import hero from "@/assets/backyard-metal-patio-cover.jpg";
import heroPicture from "@/assets/backyard-metal-patio-cover.jpg?w=640;1024;1600;1920&format=avif;webp;jpeg&as=picture";
import pergolaBlueCanopy from "@/assets/pergola-blue-canopy-poolside-twilight.jpg";
import pergolaSideShade from "@/assets/pergola-side-shade-privacy-panels-bronze.jpg";

const COVER_STYLES: {
  title: string;
  fabric: string;
  bullets: string[];
}[] = [
  {
    title: "Rod-Pocket Replacement Tops",
    fabric: "Commercial 95 or Phifertex Plus",
    bullets: [
      "Slides over your existing pergola rafters",
      "Custom cut to your length × width",
      "Block 90–95% UV — air still moves through",
      "Replace the canvas, not the whole pergola",
    ],
  },
  {
    title: "Wave / Retractable Canopy Tops",
    fabric: "Commercial 95 (knitted shade)",
    bullets: [
      "Slide-on-cable wave panels — push back for sun, pull out for shade",
      "Hand-sewn rope channels and stainless cable hardware",
      "Sized for your pergola down to the inch",
      "Easy single-person operation",
    ],
  },
  {
    title: "Fixed Stretched Shade Panels",
    fabric: "Commercial 95 or Phifertex Plus",
    bullets: [
      "Tensioned panel laced to a welded aluminum frame",
      "No flapping, no sag — sail-tight finish",
      "Best for clean modern pergolas and outdoor kitchens",
      "Marine-grade hardware throughout",
    ],
  },
  {
    title: "Vertical Drop Curtains & Wind Walls",
    fabric: "Phifertex Plus",
    bullets: [
      "Roll-up or fixed mesh side panels for sun, wind and bug control",
      "Pairs with any rod-pocket or wave top above",
      "See-through privacy — keeps the view, kills the glare",
      "Ideal for west-facing pergolas and patios",
    ],
  },
];

export const Route = createFileRoute("/pergola-covers")({
  head: (ctx) => ({
    // Self-canonical: /pergola-covers is its own leaf page.
    links: leafCanonicalLink(ctx, "/pergola-covers"),
    meta: routeMeta(ctx, {
      title: "Patio & Pergola Covers TX | Texas Made Shade",
      description:
        "Custom patio, gazebo & pergola covers for San Antonio & Austin — welded aluminum frames with Commercial 95 and Phifertex Plus tops.",
      image: hero,
      imageAlt: "Welded aluminum patio cover with custom pergola top by Texas Made Shade",
      socialTitle: "Patio, Gazebo & Pergola Covers — Texas Made Shade",
      socialDescription:
        "Welded aluminum patio covers, freestanding pavilions and custom pergola tops in Commercial 95 and Phifertex Plus — sized to your space and built in Seguin, TX.",
      keywords:
        "patio covers San Antonio, patio covers Austin, patio covers Texas, aluminum patio covers, freestanding patio cover, attached patio cover, outdoor kitchen shade, backyard pavilion Texas, pergola covers, custom pergola covers, gazebo covers, motorized patio shades Austin, motorized shades San Antonio, automatic patio shade, smart shades Austin TX, Commercial 95 pergola cover, Phifertex Plus pergola, retractable pergola canopy, replacement pergola top, awning company San Antonio, Austin awning company, Texas Made Shade",
    }),
    scripts: [
      // This route is a path parent of /pergola-covers/in/$city, so its head
      // scripts concatenate into the city matches too — only emit the hub
      // breadcrumb when /pergola-covers is the leaf page being rendered.
      ...(leafPathFromMatches(ctx) === "/pergola-covers"
        ? [pergolaBreadcrumbScript("/pergola-covers", "Patio, Gazebo & Pergola Covers")]
        : []),
      ...serviceHeadScripts({
      path: "/pergola-covers",
      serviceName: "Patio, Gazebo & Pergola Covers",
      breadcrumbName: "Patio, Gazebo & Pergola Covers",
      description:
        "Custom patio covers and pergola covers — welded aluminum patio frames, freestanding pavilions, and Commercial 95 / Phifertex Plus pergola tops in rod-pocket.",
      faqs: [
        {
          q: "What's the difference between a patio cover and a pergola cover?",
          a: "A patio cover has a solid metal or canvas top that fully blocks Texas sun and rain. A pergola cover uses a fabric top — Commercial 95 knitted shade mesh or Phifertex Plus vinyl mesh — over an open-rafter pergola, filtering light while letting heat escape. We build both on welded aluminum frames in our Seguin shop.",
        },
        {
          q: "How much does a custom patio cover cost in San Antonio, Austin or the Hill Country?",
          a: "Most residential patio covers in San Antonio, Austin and the Hill Country run $3,500–$12,000 depending on size, frame style, fabric vs metal top, and post count. Free-standing patio covers and outdoor-kitchen pavilions price higher. Send photos and rough dimensions and we'll come back with a written estimate same business day.",
        },
        {
          q: "Do I need a permit for a patio cover in San Antonio or Austin, TX?",
          a: "Most attached residential patio covers under 200 sq ft don't require a permit, but San Antonio and Austin city codes vary. We help San Antonio, Austin, New Braunfels and surrounding-city customers identify whether their patio cover build needs permitting and provide engineered drawings when required.",
        },
        {
          q: "Can you install a custom patio cover on my existing concrete slab?",
          a: "Yes — most Texas patio cover installs anchor directly to your existing concrete slab. If you don't have a slab, we coordinate with a local concrete contractor or set the patio cover on engineered footings.",
        },
        {
          q: "What fabric do you use for pergola covers?",
          a: "We build pergola covers in two fabrics: Commercial 95 (a knitted HDPE shade fabric that blocks ~95% of UV while letting air and heat escape) and Phifertex Plus (a vinyl-coated polyester mesh — heavier, longer life, more privacy). The right one depends on whether you want max airflow or max durability and screening.",
        },
        {
          q: "How much does a custom pergola cover cost?",
          a: "Replacement rod-pocket and wave tops typically run $400–$1,800 depending on size and fabric. Full pergola builds with welded aluminum frames + fabric tops generally run $4,500–$18,000. Send dimensions or a photo and we'll quote it the same business day.",
        },
        {
          q: "Can you make a cover to fit my existing pergola?",
          a: "Yes — pergola re-covers are most of what we do. We pattern Commercial 95 or Phifertex Plus to your existing posts and beams, sew rod-pocket sleeves or cable channels, and ship or install across Texas.",
        },
        {
          q: "Do pergola covers handle Texas wind and storms?",
          a: "Both fabrics are engineered for sustained sun and wind, but no fabric top is hurricane-rated. We design rod-pocket and wave tops so they can be slid back or removed in under 60 seconds before a major storm. Fixed panels use marine-grade lacing and stainless hardware.",
        },
      ],
    }).slice(0, 2),
    ],
  }),
  component: ServiceRoute,
});

function ServiceRoute() {
  const matches = useMatches();
  const leaf = matches[matches.length - 1];
  if (leaf?.routeId !== Route.id) return <Outlet />;
  return (
    <ServicePage
      currentPath="/pergola-covers"
      serviceName="Patio, Gazebo & Pergola Covers"
      eyebrow="★ PATIO, GAZEBO & PERGOLA COVERS ★"
      title="Custom Patio, Gazebo & Pergola Covers — Built for Texas"
      intro="Welded aluminum patio covers, freestanding pavilions, and custom pergola tops sewn in Commercial 95 knitted HDPE shade fabric or Phifertex Plus vinyl mesh — sized to your space and built in our Seguin, TX shop. Attached & freestanding patio covers, outdoor kitchen shade, rod-pocket and wave/retractable pergola tops, fixed shade panels and drop curtains for backyards, restaurants, resorts and HOAs across San Antonio, Austin, New Braunfels, Houston and the Hill Country."
      heroImage={heroPicture}
      hideLetsTalkForm
      preContent={
        <>
        <section className="border-t-4 border-navy-deep bg-cream py-16">
          <div className="mx-auto max-w-6xl px-4 lg:px-8">
            <figure className="sm:float-right sm:ml-6 sm:mb-4 sm:w-[48%] md:w-[44%] lg:w-[42%]">
              <img
                src={pergolaBlueCanopy}
                alt="Custom royal-blue Commercial 95 rod-pocket pergola canopy fabricated by Texas Made Shade, stretched over a cedar pergola at twilight beside a Texas backyard pool, waterfall and outdoor kitchen with string lights overhead"
                width={1400}
                height={1400}
                loading="eager"
                decoding="async"
                className="block h-auto w-full border-4 border-navy-deep object-cover shadow-card"
              />
              <figcaption className="mt-2 font-display text-[11px] uppercase tracking-[0.2em] text-navy-deep/70">
                Custom rod-pocket pergola top in royal blue Commercial 95 — Texas backyard
              </figcaption>
            </figure>
            <div className="font-display text-xs uppercase tracking-[0.3em] text-red">
              ★ Texas Gazebo &amp; Pergola Cover Specialists
            </div>
            <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">
              Custom Pergola Tops Patterned to Your Beams — Built in Seguin, Texas
            </h2>
            <p className="mt-4 text-lg text-navy-deep/80">
              From poolside cedar pergolas in the Hill Country to outdoor kitchens in San
              Antonio and resort cabanas on the Gulf Coast, Texas Made Shade hand-patterns
              every gazebo and pergola cover to your exact frame. Choose breathable
              Commercial 95 knitted HDPE shade fabric that blocks ~95% of UV while letting
              air pass through, or step up to Phifertex Plus vinyl-coated mesh when you want
              a denser, longer-lasting top with more privacy and wind diffusion.
            </p>
            <p className="mt-4 text-lg text-navy-deep/80">
              Pick your color, your style — rod-pocket replacement top, slide-on-cable wave
              canopy, fixed stretched panel or vertical drop curtain — and we&apos;ll cut and
              sew it on heavy-duty industrial machines in our Seguin shop. Marine-grade
              thread, brass grommets, stainless cable hardware, and reinforced hems built to
              ride out Texas sun, wind and thunderstorms for years.
            </p>
            <p className="mt-4 text-lg text-navy-deep/80">
              Send your pergola dimensions (length × width × beam height) and a photo of the
              frame — we&apos;ll spec the right fabric, recommend a style, and send a written
              quote the same business day. Bring your gazebo or pergola back to life without
              replacing the structure.
            </p>
            <div className="clear-both" />
          </div>
        </section>

        {/* SIDE SHADE PANELS */}
        <section className="border-t-4 border-navy-deep bg-white py-20">
          <div className="mx-auto max-w-6xl px-4 lg:px-8">
            <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
              <figure className="overflow-hidden border-4 border-navy-deep shadow-card">
                <img
                  src={pergolaSideShade}
                  alt="Three custom bronze knitted-mesh side shade privacy panels fabricated by Texas Made Shade — mounted on welded black aluminum frames against a covered patio, a black wood pergola, and a freestanding patio divider for sun blocking and privacy"
                  width={1600}
                  height={1067}
                  loading="lazy"
                  decoding="async"
                  className="block h-auto w-full object-cover"
                />
                <figcaption className="bg-navy-deep px-4 py-3 font-display text-xs uppercase tracking-[0.2em] text-white">
                  Side shade &amp; privacy panels · welded aluminum frames + knitted shade fabric
                </figcaption>
              </figure>
              <div>
                <div className="font-display text-xs uppercase tracking-[0.3em] text-red">
                  ★ Side Shade for Pergolas &amp; Gazebos
                </div>
                <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">
                  Block Low Sun &amp; Add Privacy on the Sides of Your Pergola
                </h2>
                <p className="mt-4 text-lg text-navy-deep/80">
                  A pergola top knocks out overhead sun, but Texas afternoons attack from the
                  side. We build custom side shade panels that mount to the posts of your
                  pergola, gazebo, covered patio, or free-standing on welded aluminum frames —
                  cutting low-angle glare, adding privacy from neighbors, and finishing the
                  outdoor room without closing it in.
                </p>
                <ul className="mt-6 grid gap-3 text-base text-navy-deep/85">
                  <li className="flex items-start gap-2">
                    <Check className="mt-1 h-4 w-4 shrink-0 text-red" />
                    Commercial 95 knitted shade fabric or Phifertex Plus mesh
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="mt-1 h-4 w-4 shrink-0 text-red" />
                    Mount to existing pergola/gazebo posts or freestanding welded frame
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="mt-1 h-4 w-4 shrink-0 text-red" />
                    Powder-coated aluminum frames in black, bronze, or custom color
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="mt-1 h-4 w-4 shrink-0 text-red" />
                    Reinforced hems, stainless grommets, and lockable lacing or bungee
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="mt-1 h-4 w-4 shrink-0 text-red" />
                    Sized to your opening — single panel, multi-panel, or full wrap
                  </li>
                </ul>
                <div className="mt-6 flex flex-wrap gap-3">
                  <a
                    href={`/#lets-talk?type=${encodeURIComponent("Pergola Side Shade Panels")}`}
                    className="inline-flex items-center bg-red px-6 py-3 font-display text-sm uppercase tracking-wider text-white transition hover:bg-red/90"
                  >
                    ★ Quote a Side Shade Panel
                  </a>
                  <a
                    href="tel:+18303909588"
                    className="inline-flex items-center border-2 border-navy-deep bg-white px-6 py-3 font-display text-sm uppercase tracking-wider text-navy-deep transition hover:bg-navy-deep hover:text-white"
                  >
                    Call +1 (830) 390-9588
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
        </>
      }
      heroCta={{
        eyebrow: "Free, no-pressure estimate",
        headline: "Get a Free Pergola Cover Quote",
        subhead:
          "Send your pergola dimensions (length × width) and a photo. We'll spec the fabric, style and color and quote it same business day.",
        primaryHref: `/#lets-talk?type=${encodeURIComponent("Pergola Cover")}`,
        primaryLabel: "Get a Free Pergola Quote",
        phoneNumber: "+1 (830) 390-9588",
      }}
      features={[
        "Two premium fabrics — Commercial 95 & Phifertex Plus",
        "Rod-pocket replacement tops sized to your pergola",
        "Wave / retractable cable-slide canopies",
        "Fixed stretched shade panels on welded frames",
        "Vertical drop curtains and wind walls",
        "Patterned in your color — sewn in Seguin, TX",
        "Marine-grade thread, hardware and stainless cable",
        "Re-cover an existing pergola or build new from scratch",
      ]}
      body={[
        {
          heading: "Commercial 95 — Knitted HDPE Shade Fabric (95% UV Block)",
          text: "Commercial 95 is the workhorse pergola fabric — a knitted HDPE mesh that blocks roughly 95% of UV while letting air and heat pass through. It stays cool to the touch, drops surface temps under the pergola by 15–25°F, and shrugs off Texas sun for years. Best for rod-pocket replacement tops and wave/retractable canopies where breathability matters more than full rain protection.",
          bullets: [
            "Blocks ~95% of UV — full shade with airflow",
            "Knitted construction won't fray or unravel if punctured",
            "10-year fabric warranty against UV breakdown",
            "Lighter weight — easier to retract, slide or remove for storms",
            "10+ stocked colors — earth tones, greens, blues, charcoal, black",
          ],
        },
        {
          heading: "Phifertex Plus — Vinyl-Coated Polyester Mesh",
          text: "Phifertex Plus is the heavy-hitter — a vinyl-coated polyester mesh originally engineered for marine and commercial outdoor furniture. It's denser, more abrasion-resistant and offers more visual privacy than Commercial 95, with a longer service life in coastal and high-UV environments. Best for fixed stretched shade panels, drop curtains and any pergola cover that needs to look tailored and last 12–15+ years.",
          bullets: [
            "Vinyl-coated polyester — exceptional UV and mildew resistance",
            "Denser weave — better privacy and wind diffusion",
            "Marine-grade — stands up to coastal salt air",
            "Cleans with soap and water; bleach-safe",
            "Wide tweed and solid color range to coordinate with sling furniture",
          ],
        },
        {
          heading: "Pick the Fabric, Pick the Style — Built to Your Pergola",
          text: "Tell us your pergola size, the look you want and how often you'll use it. We'll recommend the right fabric (Commercial 95 vs Phifertex Plus) and the right cover style — rod-pocket, wave, fixed panel or drop curtain — and pattern the whole top to fit your beams down to the inch.",
        },
      ]}
      applications={[
        "Backyard Pergolas",
        "Outdoor Kitchens",
        "Pool Decks & Cabanas",
        "Fire Pit & Lounge Areas",
        "Restaurant & Bar Patios",
        "Hotel & Resort Courtyards",
        "Apartment & HOA Common Areas",
        "Pre-Built Pergola Re-Covers",
      ]}
      cityCallouts={[
        {
          to: "/san-antonio",
          city: "San Antonio",
          anchorText: "Pergola covers in San Antonio, TX",
          blurb:
            "Custom Commercial 95 and Phifertex Plus pergola tops for San Antonio backyards, outdoor kitchens and pool decks across Bexar County.",
        },
        {
          to: "/austin",
          city: "Austin",
          anchorText: "Pergola covers in Austin, TX",
          blurb:
            "Pergola covers and replacement canopies for Austin and Travis County — wave, rod-pocket and stretched panel styles.",
        },
        {
          to: "/new-braunfels",
          city: "New Braunfels",
          anchorText: "Pergola covers in New Braunfels",
          blurb:
            "Pergola tops for New Braunfels and Comal County — riverfront, ranch and residential builds, sewn in nearby Seguin.",
        },
        {
          to: "/houston",
          city: "Houston",
          anchorText: "Pergola covers in Houston",
          blurb:
            "Coastal-grade Phifertex Plus pergola covers engineered for Gulf Coast humidity and salt air — River Oaks, Memorial, Katy and Sugar Land.",
        },
        {
          to: "/seguin",
          city: "Seguin",
          anchorText: "Pergola covers built in our Seguin shop",
          blurb:
            "Pergola tops patterned, cut and sewn in our Seguin, TX shop and installed across the I-10 / I-35 corridor.",
        },
      ]}
      postCta={(
        <>
          {/* COVER STYLES */}
          <section className="border-t-4 border-navy-deep bg-cream py-20">
            <div className="mx-auto max-w-6xl px-4 lg:px-8">
              <div className="text-center">
                <div className="font-display text-sm tracking-[0.3em] text-red">
                  ★ PERGOLA COVER STYLES
                </div>
                <h2 className="mt-2 font-display text-4xl font-bold text-navy-deep md:text-5xl">
                  Pick the Cover Style That Fits Your Pergola
                </h2>
                <p className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground">
                  Four ways we build pergola tops in Commercial 95 and Phifertex Plus —
                  patterned to your beams and sewn in our Seguin shop.
                </p>
              </div>

              <div className="mt-12 grid gap-6 md:grid-cols-2">
                {COVER_STYLES.map((style) => (
                  <article
                    key={style.title}
                    className="border-l-4 border-red bg-white p-6 shadow-card"
                  >
                    <div className="font-display text-xs uppercase tracking-[0.2em] text-red">
                      {style.fabric}
                    </div>
                    <h3 className="mt-2 font-display text-xl font-bold text-navy-deep">
                      {style.title}
                    </h3>
                    <ul className="mt-4 space-y-2 text-base text-navy-deep/85">
                      {style.bullets.map((b) => (
                        <li key={b} className="flex items-start gap-2">
                          <Check className="mt-1 h-4 w-4 shrink-0 text-red" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section className="border-t-4 border-navy-deep bg-white py-20">
            <div className="mx-auto max-w-4xl px-4 text-center lg:px-8">
              <div className="font-display text-sm tracking-[0.3em] text-red">
                ★ FABRICS WE USE
              </div>
              <h2 className="mt-2 font-display text-4xl font-bold text-navy-deep md:text-5xl">
                Built with Commercial-Grade Shade Mesh
              </h2>
              <p className="mx-auto mt-5 text-lg text-muted-foreground">
                Every gazebo and pergola top we build is sewn from one of the
                outdoor mesh fabrics we trust day in, day out:{" "}
                <strong className="text-navy-deep">Commercial 95</strong>,{" "}
                <strong className="text-navy-deep">Phifertex Plus &amp; Standard</strong>,{" "}
                <strong className="text-navy-deep">Textilene</strong>, and other
                premium architectural meshes.
              </p>
              <p className="mx-auto mt-4 text-base text-muted-foreground">
                We'll help you pick the right weave, UV-block rating and color
                for your structure, sun exposure and budget. Free swatches
                mailed on request.
              </p>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-navy-deep px-6 py-3 font-display text-sm uppercase tracking-wider text-white transition hover:bg-navy-deep/90"
                >
                  Request Free Fabric Swatches
                </Link>
              </div>
            </div>
          </section>

          {/* RELATED SERVICES */}
          <section className="border-t-4 border-navy-deep bg-cream py-20">
            <div className="mx-auto max-w-6xl px-4 lg:px-8">
              <div className="text-center">
                <div className="font-display text-sm tracking-[0.3em] text-red">
                  ★ RELATED SERVICES
                </div>
                <h2 className="mt-2 font-display text-4xl font-bold text-navy-deep md:text-5xl">
                  Pairs Well With
                </h2>
              </div>

              <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                <Link
                  to="/pergola-covers"
                  className="group block border-l-4 border-red bg-white p-6 shadow-card transition hover:bg-navy-deep hover:text-white"
                >
                  <h3 className="font-display text-xl font-bold text-navy-deep group-hover:text-white">
                    Welded Aluminum Patio Covers
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground group-hover:text-white/90">
                    Solid-roof patio covers when you want full sun and rain protection
                    instead of fabric.
                  </p>
                </Link>
                <Link
                  to="/awnings"
                  className="group block border-l-4 border-red bg-white p-6 shadow-card transition hover:bg-navy-deep hover:text-white"
                >
                  <h3 className="font-display text-xl font-bold text-navy-deep group-hover:text-white">
                    Custom Awnings
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground group-hover:text-white/90">
                    Welded-frame awnings in Sunbrella® and Tempotest canvas for windows,
                    doors and storefronts.
                  </p>
                </Link>
                <Link
                  to="/sail-shades"
                  className="group block border-l-4 border-red bg-white p-6 shadow-card transition hover:bg-navy-deep hover:text-white"
                >
                  <h3 className="font-display text-xl font-bold text-navy-deep group-hover:text-white">
                    Sail Shades
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground group-hover:text-white/90">
                    Tensioned shade sails — pairs naturally with Commercial 95 fabric and
                    welded posts.
                  </p>
                </Link>
                <Link
                  to="/curtains"
                  className="group block border-l-4 border-red bg-white p-6 shadow-card transition hover:bg-navy-deep hover:text-white"
                >
                  <h3 className="font-display text-xl font-bold text-navy-deep group-hover:text-white">
                    Roll-Down Curtains
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground group-hover:text-white/90">
                    Clear-vinyl and Phifertex drop curtains — perfect side walls for any
                    pergola.
                  </p>
                </Link>
                <Link
                  to="/custom-fabrication"
                  className="group block border-l-4 border-red bg-white p-6 shadow-card transition hover:bg-navy-deep hover:text-white"
                >
                  <h3 className="font-display text-xl font-bold text-navy-deep group-hover:text-white">
                    Welding &amp; Custom Fabrication
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground group-hover:text-white/90">
                    Welded aluminum pergola frames, posts, brackets and one-off
                    fabrication.
                  </p>
                </Link>
                <Link
                  to="/seguin"
                  className="group block border-l-4 border-red bg-white p-6 shadow-card transition hover:bg-navy-deep hover:text-white"
                >
                  <h3 className="font-display text-xl font-bold text-navy-deep group-hover:text-white">
                    Visit the Seguin, TX Shop
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground group-hover:text-white/90">
                    Walk in, see the fabric in person, talk through your pergola in our
                    Seguin shop.
                  </p>
                </Link>
              </div>
            </div>
          </section>
        <PergolaUpgradeCta />
        </>
      )}
    >
      <GazeboCoverForm />
    </ServicePage>
  );
}

